<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isPasswordVisible = ref(false)
const isLoading = ref(false)
const isAutoLoggingIn = ref(false)
const router = useRouter()
const ability = useAbility()

const errors = ref({
  email: undefined,
  password: undefined,
})

const refVForm = ref()

const credentials = ref({
  email: 'innuoendy@salubris.au',
  password: 'admin',
})

const rememberMe = ref(false)

// Check for saved credentials on mount
onMounted(async () => {
  const savedCredentials = localStorage.getItem('innuoendy_credentials')
  const autoLogin = localStorage.getItem('innuoendy_auto_login')

  if (savedCredentials && autoLogin === 'true') {
    try {
      const saved = JSON.parse(savedCredentials)
      credentials.value = saved
      isAutoLoggingIn.value = true

      console.log('🔐 Auto-login enabled, attempting authentication...')
      await login(true)
    } catch (err) {
      console.error('Auto-login failed:', err)
      isAutoLoggingIn.value = false
    }
  }
})

const login = async (isAuto = false) => {
  if (isLoading.value) return

  isLoading.value = true
  errors.value = { email: undefined, password: undefined }

  try {
    console.log('🌿 Accessing Innuoendy Operations Center...')

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: credentials.value.email,
        password: credentials.value.password,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()

      if (errorData.errors) {
        errors.value = errorData.errors
      } else {
        errors.value = {
          email: ['Access denied. Please verify your credentials.'],
        }
      }

      // Clear saved credentials on failed login
      if (isAuto) {
        localStorage.removeItem('innuoendy_credentials')
        localStorage.removeItem('innuoendy_auto_login')
      }

      isAutoLoggingIn.value = false
      return
    }

    const data = await response.json()
    console.log('✅ Authentication successful')

    const { accessToken, userData, userAbilityRules } = data

    // Store auth data
    useCookie('userAbilityRules').value = userAbilityRules
    useCookie('userData').value = userData
    useCookie('accessToken').value = accessToken

    // Save credentials if remember me is checked
    if (rememberMe.value || isAuto) {
      localStorage.setItem(
        'innuoendy_credentials',
        JSON.stringify({
          email: credentials.value.email,
          password: credentials.value.password,
        })
      )
      localStorage.setItem('innuoendy_auto_login', 'true')
      localStorage.setItem('innuoendy_last_login', new Date().toISOString())
      console.log('💾 Credentials saved for auto-login')
    } else {
      localStorage.removeItem('innuoendy_credentials')
      localStorage.removeItem('innuoendy_auto_login')
    }

    // Update abilities
    ability.update(userAbilityRules)

    console.log('🚀 Redirecting to operations dashboard...')

    // Smooth transition to dashboard
    await nextTick()
    router.replace('/dashboards/home')
  } catch (err) {
    console.error('❌ Login error:', err)
    errors.value = {
      email: ['Network connection failed. Please check connectivity.'],
    }
    isAutoLoggingIn.value = false
  } finally {
    isLoading.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login()
  })
}

const clearSavedCredentials = () => {
  localStorage.removeItem('innuoendy_credentials')
  localStorage.removeItem('innuoendy_auto_login')
  localStorage.removeItem('innuoendy_last_login')
  rememberMe.value = false
  console.log('🗑️ Saved credentials cleared')
}

// Check if credentials are saved
const hasSavedCredentials = computed(() => {
  return localStorage.getItem('innuoendy_credentials') !== null
})
</script>

<template>
  <div class="regenitrac-auth-container">
    <!-- Auto-login Overlay -->
    <div v-if="isAutoLoggingIn" class="auto-login-overlay">
      <div class="auto-login-content">
        <VAvatar size="120" color="success" variant="tonal" class="mb-6">
          <VIcon icon="ri-leaf-line" size="60" />
        </VAvatar>
        <h2 class="text-h4 mb-4 font-weight-bold">Innuoendy Operations</h2>
        <VProgressCircular
          indeterminate
          color="success"
          size="48"
          width="4"
          class="mb-4"
        />
        <p class="text-body-1 text-medium-emphasis">
          Connecting to environmental monitoring systems...
        </p>
      </div>
    </div>

    <!-- Regular Login UI -->
    <div v-else>
      <RouterLink to="/" class="regenitrac-auth-header">
        <div class="auth-logo d-flex align-center gap-x-3">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <div class="brand-identity">
            <h1 class="auth-title text-h5 font-weight-bold">
              {{ themeConfig.app.title }}
            </h1>
            <span class="brand-tagline">Environmental Operations Hub</span>
          </div>
        </div>
      </RouterLink>

      <VRow no-gutters class="auth-wrapper regenitrac-auth-wrapper">
        <VCol
          cols="12"
          md="12"
          class="auth-card-v2 d-flex align-center justify-center"
        >
          <VCard
            flat
            :max-width="520"
            class="mt-12 mt-sm-0 pa-6 regenitrac-login-card"
            elevation="0"
          >
            <!-- Welcome Header -->
            <VCardText class="text-center pb-4">
              <div class="regenitrac-welcome-section mb-6">
                <div class="welcome-icon mb-4">
                  <VAvatar size="80" color="success" variant="tonal">
                    <VIcon icon="ri-leaf-line" size="40" />
                  </VAvatar>
                </div>
                <h2 class="text-h4 mb-3 font-weight-bold">
                  Welcome to <span class="text-success">Innuoendy</span>
                </h2>
                <p class="text-body-1 text-medium-emphasis">
                  Environmental monitoring and operations management
                </p>
              </div>

              <!-- Status Indicators -->
              <div class="status-indicators mb-6">
                <VRow>
                  <VCol cols="6" sm="3">
                    <div class="status-item">
                      <VIcon
                        icon="ri-cloud-line"
                        color="info"
                        size="20"
                        class="mb-1"
                      />
                      <div class="text-caption font-weight-medium">Weather</div>
                    </div>
                  </VCol>
                  <VCol cols="6" sm="3">
                    <div class="status-item">
                      <VIcon
                        icon="ri-task-line"
                        color="warning"
                        size="20"
                        class="mb-1"
                      />
                      <div class="text-caption font-weight-medium">Tasks</div>
                    </div>
                  </VCol>
                  <VCol cols="6" sm="3">
                    <div class="status-item">
                      <VIcon
                        icon="ri-map-pin-line"
                        color="error"
                        size="20"
                        class="mb-1"
                      />
                      <div class="text-caption font-weight-medium">Assets</div>
                    </div>
                  </VCol>
                  <VCol cols="6" sm="3">
                    <div class="status-item">
                      <VIcon
                        icon="ri-wifi-line"
                        color="success"
                        size="20"
                        class="mb-1"
                      />
                      <div class="text-caption font-weight-medium">Network</div>
                    </div>
                  </VCol>
                </VRow>
              </div>
            </VCardText>

            <!-- Login Form -->
            <VCardText>
              <VForm ref="refVForm" @submit.prevent="onSubmit">
                <VRow>
                  <VCol cols="12">
                    <VTextField
                      v-model="credentials.email"
                      label="Email Address"
                      placeholder="your.email@salubris.au"
                      type="email"
                      autofocus
                      variant="outlined"
                      color="success"
                      :rules="[requiredValidator, emailValidator]"
                      :error-messages="errors.email"
                      :disabled="isLoading"
                      prepend-inner-icon="ri-mail-line"
                      class="regenitrac-text-field"
                    />
                  </VCol>

                  <VCol cols="12">
                    <VTextField
                      v-model="credentials.password"
                      label="Password"
                      placeholder="Enter your access code"
                      variant="outlined"
                      color="success"
                      :rules="[requiredValidator]"
                      :type="isPasswordVisible ? 'text' : 'password'"
                      :error-messages="errors.password"
                      :disabled="isLoading"
                      prepend-inner-icon="ri-lock-line"
                      :append-inner-icon="
                        isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'
                      "
                      @click:append-inner="
                        isPasswordVisible = !isPasswordVisible
                      "
                      class="regenitrac-text-field"
                    />

                    <div
                      class="d-flex align-center flex-wrap justify-space-between my-6 gap-2"
                    >
                      <VCheckbox
                        v-model="rememberMe"
                        color="success"
                        density="compact"
                        :disabled="isLoading"
                      >
                        <template #label>
                          <div class="d-flex align-center gap-2">
                            <span>Auto-login on this device</span>
                            <VTooltip>
                              <template #activator="{ props }">
                                <VIcon
                                  v-bind="props"
                                  icon="ri-information-line"
                                  size="16"
                                />
                              </template>
                              <div
                                class="text-caption"
                                style="max-width: 250px"
                              >
                                Enable for kiosk/display mode. Credentials will
                                be saved locally for automatic login.
                              </div>
                            </VTooltip>
                          </div>
                        </template>
                      </VCheckbox>

                      <VBtn
                        v-if="hasSavedCredentials"
                        variant="text"
                        size="small"
                        color="error"
                        @click="clearSavedCredentials"
                      >
                        Clear Saved Login
                      </VBtn>
                    </div>

                    <VBtn
                      block
                      type="submit"
                      color="success"
                      size="large"
                      :loading="isLoading"
                      :disabled="isLoading"
                      class="mb-4 regenitrac-login-btn"
                    >
                      <VIcon start icon="ri-login-box-line" />
                      {{
                        isLoading
                          ? 'Connecting to Systems...'
                          : 'Access Dashboard'
                      }}
                    </VBtn>
                  </VCol>
                </VRow>
              </VForm>
            </VCardText>

            <!-- Footer -->
            <VCardText class="text-center pt-0">
              <div class="regenitrac-auth-footer">
                <!-- Security Notice -->
                <VAlert
                  color="success"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                  border="start"
                >
                  <template #prepend>
                    <VIcon icon="ri-shield-check-line" size="16" />
                  </template>
                  <div class="text-caption">
                    <strong>Secure Connection</strong> • Traditional lands of
                    the Wongatha People
                  </div>
                </VAlert>

                <!-- Display Mode Notice -->
                <VAlert
                  v-if="hasSavedCredentials"
                  color="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  <template #prepend>
                    <VIcon icon="ri-tv-line" size="16" />
                  </template>
                  <div class="text-caption">
                    <strong>Display Mode Active</strong> • Auto-login configured
                  </div>
                </VAlert>

                <!-- System Info -->
                <div class="company-info mb-4">
                  <p
                    class="text-body-2 text-medium-emphasis mb-2 font-weight-medium"
                  >
                    Salubris Australia • Innuoendy Site
                  </p>
                  <p class="text-caption text-medium-emphasis">
                    Weather • Tasks • Asset Tracking • Connectivity
                  </p>
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@core/scss/template/pages/page-auth.scss';

.regenitrac-auth-container {
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-background)) 0%,
    rgba(var(--v-theme-success), 0.02) 100%
  );
  position: relative;
}

.auto-login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-background)) 0%,
    rgba(var(--v-theme-success), 0.05) 100%
  );
  z-index: 9999;
  animation: fadeIn 0.5s ease;
}

.auto-login-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem;
  text-align: center;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.regenitrac-auth-header {
  text-decoration: none;
  color: inherit;
  padding: 2rem;
  display: block;

  .brand-identity {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .auth-title {
      margin-bottom: 0;
      line-height: 1.2;
      color: rgb(var(--v-theme-on-surface));
    }

    .brand-tagline {
      font-size: 0.875rem;
      color: rgb(var(--v-theme-success));
      font-weight: 500;
      margin-top: 2px;
      opacity: 0.9;
    }
  }
}

.regenitrac-auth-wrapper {
  min-height: calc(100vh - 120px);
}

.regenitrac-login-card {
  border-radius: 20px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-success), 0.1);
  box-shadow: 0 8px 32px rgba(var(--v-theme-success), 0.08);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    box-shadow: 0 12px 48px rgba(var(--v-theme-success), 0.12);
    transform: translateY(-2px);
  }
}

.status-indicators {
  .status-item {
    text-align: center;
    padding: 0.5rem;
  }
}

.regenitrac-text-field {
  .v-field {
    border-radius: 12px;
    border: 1px solid rgba(var(--v-theme-success), 0.2);
  }

  .v-field--focused {
    box-shadow: 0 0 0 2px rgba(var(--v-theme-success), 0.1);
    border-color: rgb(var(--v-theme-success));
  }
}

.regenitrac-login-btn {
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-success)) 0%,
    rgba(var(--v-theme-success-darken-1)) 100%
  );
  box-shadow: 0 4px 12px rgba(var(--v-theme-success), 0.3);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(var(--v-theme-success), 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
  }
}

.regenitrac-auth-footer {
  margin-top: 1.5rem;

  .company-info {
    padding: 1rem 0;
    border-top: 1px solid rgba(var(--v-theme-outline-variant), 0.3);
    border-bottom: 1px solid rgba(var(--v-theme-outline-variant), 0.3);
  }
}

.v-theme--dark {
  .regenitrac-auth-container {
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-background)) 0%,
      rgba(var(--v-theme-success), 0.05) 100%
    );
  }

  .regenitrac-login-card {
    background: rgba(var(--v-theme-surface), 0.95);
    backdrop-filter: blur(10px);
    border-color: rgba(var(--v-theme-success), 0.2);
  }

  .auto-login-overlay {
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-background)) 0%,
      rgba(var(--v-theme-success), 0.08) 100%
    );
  }
}

@media (max-width: 600px) {
  .regenitrac-auth-header {
    padding: 1rem;

    .auth-logo {
      flex-direction: column;
      text-align: center;
      gap: 0.5rem !important;
    }

    .brand-identity {
      align-items: center;
    }
  }

  .regenitrac-login-card {
    margin: 1rem;
    padding: 1.5rem !important;
    max-width: calc(100vw - 2rem) !important;
  }

  .status-indicators {
    .v-col {
      padding: 0.25rem;
    }
  }
}
</style>
